import React, { useMemo, useState } from 'react';

import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import {
  ActivityTimelinePoint,
  ANNOTATIONS_BASE_QUERY_KEY,
  getAnnotationAssignmentActivityTimeline,
} from 'api/annotationAssignments';
import {
  BarElement,
  CategoryScale,
  Chart,
  ChartOptions,
  Tooltip as ChartTooltip,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
} from 'chart.js';
import { first, flatMap, forEach, isEmpty, keys, map, orderBy, uniq } from 'es-toolkit/compat';
import { AnnotationAssignment, AnnotationWorkingStates } from 'interfaces/annotation';
import { Chart as ReactChart } from 'react-chartjs-2';
import { getAnnotationsClassDisplayName, getTodoClassKey } from 'services/annotations/helpers';
import { formatDateForUser } from 'utils/dateTime';
import useTaxonomy from 'utils/queryHooks/taxonomy/useTaxonomy';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';

import {
  getTimelineChartTitle,
  getTimelineSeriesLabel,
  getTimelineYAxisLabel,
  TIMELINE_BREAKDOWN_LABELS,
  TIMELINE_VIEW_CONFIG,
  TimelineBreakdown,
  TimelineView,
} from './AnnotationAssignmentTimelineChart.utils';

Chart.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, ChartTooltip, Legend);

const CHART_HEIGHT = 300;

const WORKING_STATE_LABELS: Record<string, string> = {
  [AnnotationWorkingStates.Annotating]: 'Annotating',
  [AnnotationWorkingStates.QA]: 'QA',
  [AnnotationWorkingStates.Done]: 'Done',
};

const SERIES_PALETTE = [
  '#4e79a7',
  '#f28e2b',
  '#e15759',
  '#76b7b2',
  '#59a14f',
  '#edc948',
  '#b07aa1',
  '#ff9da7',
  '#9c755f',
  '#bab0ac',
  '#af7aa1',
  '#d37295',
  '#86bcb6',
  '#8cd17d',
  '#b6992d',
];

interface AnnotationAssignmentTimelineChartProps {
  annotationAssignment: AnnotationAssignment;
}

export const AnnotationAssignmentTimelineChart: React.FC<AnnotationAssignmentTimelineChartProps> = ({
  annotationAssignment,
}) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState<TimelineView>('workflow');
  const [breakdown, setBreakdown] = useState<TimelineBreakdown>(TIMELINE_VIEW_CONFIG.workflow.defaultBreakdown);

  const { idToDisplayNameMap: taxonomyIdToDisplayNameMap } = useTaxonomy();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();

  const annotationAssignmentId = annotationAssignment?.annotationAssignmentId;
  const viewConfig = TIMELINE_VIEW_CONFIG[view];
  const selectedMetric = viewConfig.metric;
  const allowedBreakdowns = viewConfig.breakdowns;

  const { data: timelineData, isLoading } = useQuery(
    [ANNOTATIONS_BASE_QUERY_KEY, 'activityTimeline', annotationAssignmentId],
    ({ signal }) =>
      getAnnotationAssignmentActivityTimeline({
        annotationAssignmentId,
        signal,
      }),
    {
      enabled: expanded && !!annotationAssignmentId,
      staleTime: 60_000,
    }
  );

  const points = timelineData?.points;

  const textColor = theme.palette.text.secondary;
  const gridColor = theme.palette.divider;

  const todoColorByKey = useMemo(() => {
    const colorByKey: Record<string, string | undefined> = {};
    forEach(first(annotationAssignment?.todos)?.options, (todoOption) => {
      const todoClassKey = getTodoClassKey(todoOption);
      colorByKey[todoClassKey] = todoOption.color;
      if (!colorByKey[todoOption.name]) {
        colorByKey[todoOption.name] = todoOption.color;
      }
    });
    return colorByKey;
  }, [annotationAssignment]);

  const workingStateColors: Record<string, string> = useMemo(
    () => ({
      [AnnotationWorkingStates.Annotating]: theme.palette.primary.main,
      [AnnotationWorkingStates.QA]: theme.palette.info.main,
      [AnnotationWorkingStates.Done]: theme.palette.success.main,
    }),
    [theme]
  );

  const getPointValue = (point: ActivityTimelinePoint): number => {
    if (selectedMetric === 'accumulated') return point.snapshot?.totalSlides || 0;
    if (selectedMetric === 'slides') return point.totalSlides;
    if (selectedMetric === 'annotations') return point.totalAnnotations;
    return point.totalChanges;
  };

  const { chartData, chartOptions } = useMemo(() => {
    if (!points || isEmpty(points)) {
      return { chartData: null, chartOptions: null };
    }

    const labels = map(points, (point) => formatDateForUser(point.date, { month: 'short', day: 'numeric' }));
    const isAccumulated = selectedMetric === 'accumulated';

    const buildDatasets = () => {
      if (breakdown === 'total') {
        return [
          {
            type: 'bar' as const,
            label: getTimelineSeriesLabel(view),
            data: map(points, getPointValue),
            backgroundColor: `${theme.palette.primary.main}99`,
            borderColor: theme.palette.primary.main,
            borderWidth: 1,
            borderRadius: 3,
          },
        ];
      }

      if (breakdown === 'tagger') {
        const allTaggers = orderBy(
          uniq(flatMap(points, (point) => keys(isAccumulated ? point.snapshot?.byTagger || {} : point.byTagger)))
        );

        return map(allTaggers, (taggerName, index) => {
          const data = map(points, (point) => {
            if (isAccumulated) {
              return point.snapshot?.byTagger?.[taggerName] || 0;
            }

            const group = point.byTagger[taggerName];
            if (!group) return 0;

            return selectedMetric === 'slides'
              ? group.slides
              : selectedMetric === 'annotations'
              ? group.annotations
              : group.changes;
          });

          return {
            type: 'bar' as const,
            label: taggerName,
            data,
            backgroundColor: `${SERIES_PALETTE[index % SERIES_PALETTE.length]}99`,
            borderColor: SERIES_PALETTE[index % SERIES_PALETTE.length],
            borderWidth: 1,
            borderRadius: 3,
          };
        });
      }

      if (breakdown === 'workingState') {
        const allStates = uniq(
          flatMap(points, (point) => keys(isAccumulated ? point.snapshot?.byWorkingState || {} : point.byWorkingState))
        );

        return map(allStates, (state) => {
          const data = map(points, (point) => {
            if (isAccumulated) {
              return point.snapshot?.byWorkingState?.[state] || 0;
            }

            const group = point.byWorkingState[state];
            if (!group) return 0;

            return selectedMetric === 'slides'
              ? group.slides
              : selectedMetric === 'annotations'
              ? group.annotations
              : group.changes;
          });

          return {
            type: 'bar' as const,
            label: WORKING_STATE_LABELS[state] || state,
            data,
            backgroundColor: `${workingStateColors[state] || theme.palette.grey[500]}99`,
            borderColor: workingStateColors[state] || theme.palette.grey[500],
            borderWidth: 1,
            borderRadius: 3,
          };
        });
      }

      const allClassKeys = orderBy(
        uniq(flatMap(points, (point) => keys(isAccumulated ? point.snapshot?.byClass || {} : point.byClass)))
      );
      const classLabels = map(allClassKeys, (classKey) =>
        getAnnotationsClassDisplayName(classKey, taxonomyIdToDisplayNameMap, stainTypeIdToDisplayName, allClassKeys)
      );

      return map(allClassKeys, (classKey, index) => {
        const color = todoColorByKey[classKey] || SERIES_PALETTE[index % SERIES_PALETTE.length];

        return {
          type: 'bar' as const,
          label: classLabels[index],
          data: map(points, (point) =>
            isAccumulated ? point.snapshot?.byClass?.[classKey] || 0 : point.byClass[classKey] || 0
          ),
          backgroundColor: `${color}99`,
          borderColor: color,
          borderWidth: 1,
          borderRadius: 3,
        };
      });
    };

    return {
      chartData: {
        labels,
        datasets: buildDatasets(),
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index' as const, intersect: false },
        plugins: {
          title: {
            display: true,
            text: getTimelineChartTitle(view, breakdown),
            color: textColor,
            font: { size: 13 },
          },
          legend: {
            display: breakdown !== 'total',
            position: 'bottom' as const,
            labels: { color: textColor, boxWidth: 12, padding: 12 },
          },
          datalabels: { display: false },
        },
        scales: {
          x: {
            stacked: breakdown !== 'total',
            ticks: { color: textColor, maxRotation: 45 },
            grid: { color: gridColor },
          },
          y: {
            stacked: breakdown !== 'total',
            type: 'linear' as const,
            position: 'left' as const,
            title: {
              display: true,
              text: getTimelineYAxisLabel(view),
              color: textColor,
            },
            ticks: { color: textColor },
            grid: { color: gridColor },
            beginAtZero: true,
          },
        },
      } as ChartOptions,
    };
  }, [
    breakdown,
    gridColor,
    points,
    selectedMetric,
    stainTypeIdToDisplayName,
    taxonomyIdToDisplayNameMap,
    textColor,
    theme,
    todoColorByKey,
    view,
    workingStateColors,
  ]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title={expanded ? 'Hide annotation workflow timeline' : 'Show annotation workflow timeline'}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={(currentTheme) => ({
              borderRadius: '4px',
              backgroundColor: expanded
                ? currentTheme.palette.action.selected
                : currentTheme.palette.mode === 'light'
                ? currentTheme.palette.grey[200]
                : currentTheme.palette.grey[900],
            })}
          >
            <TimelineRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="body2" color="text.secondary">
          Workflow &amp; Activity Timeline
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Track
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={view}
                onChange={(_event, nextView: TimelineView | null) => {
                  if (!nextView) return;
                  setView(nextView);
                  setBreakdown(TIMELINE_VIEW_CONFIG[nextView].defaultBreakdown);
                }}
              >
                {map(TIMELINE_VIEW_CONFIG, (config, timelineViewKey) => (
                  <ToggleButton key={timelineViewKey} value={timelineViewKey}>
                    {config.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {allowedBreakdowns.length > 1 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  Break down
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={breakdown}
                  onChange={(_event, nextBreakdown: TimelineBreakdown | null) => {
                    if (nextBreakdown) setBreakdown(nextBreakdown);
                  }}
                >
                  {map(allowedBreakdowns, (breakdownKey) => (
                    <ToggleButton key={breakdownKey} value={breakdownKey}>
                      {TIMELINE_BREAKDOWN_LABELS[breakdownKey]}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {viewConfig.description}
          </Typography>

          {isLoading ? (
            <Box sx={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={32} />
            </Box>
          ) : chartData ? (
            <Box sx={{ height: CHART_HEIGHT, position: 'relative' }}>
              <ReactChart type="bar" data={chartData} options={chartOptions} />
            </Box>
          ) : (
            <Box sx={{ height: CHART_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography color="text.secondary">No saved annotation activity yet</Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default AnnotationAssignmentTimelineChart;
